import { useState, useCallback } from "react";
import { blankProduct, Driver, Preparer, Tractor, Sprayer } from "../types";

import { useQueryRetrieveCultureStages, useQueryRetrievePests, useQueryRetrieveProducts, useQueryRetrieveUnits, useQueryRetrieveFarmDetails, useQueryRetrieveFormulations, useQueryRetrieveProductIngredients, useQueryRetrieveIngredients, useQueryRetrieveCultures, useQueryRetrieveBlocks, useQueryRetrieveVarieties, useQueryRetrieveTrees } from "../services/stores/queries";
import { useQueryClient } from "@tanstack/react-query";


 /**
* A forceUpdate hook
*/
export const useForceUpdate = () => {
    const [, setValue] = useState(0);
    return () => setValue(value => value + 1); // update state to force render
  }

  /**
   * 
   * @returns 
   */
export const useProducts = () => {
    const { data: products,
      error: errorProducts,
      isLoading: isLoadingProducts
     } = useQueryRetrieveProducts();

    const toProduct = (productID: string | undefined) => {
        if (products && productID)
            return products.filter(p => p.productID === productID)[0]
        else
            return blankProduct
    }

    const toProductName = (productID: string | undefined) => {
        return toProduct(productID)?.productName
    }

    return { products, errorProducts, isLoadingProducts, toProduct, toProductName }
  }


  /**
   * 
   * @returns 
   */
  export const useUnits = () => {
    const { data: units,
      error: errorUnits,
      isLoading: isLoadingUnits
     } = useQueryRetrieveUnits();

    const toUnit = (unitID: string | undefined) => {
        if (units && unitID)
            return units.filter(u=> u.unitID === unitID)[0]
        else
            return undefined
    }

    const toUnitName = (unitID: string | undefined) => {
        return toUnit(unitID)?.unitShortName
    }
   
    const toUnitLongName = (unitID: string | undefined) => {
      return toUnit(unitID)?.unitName
   }

    return { units, errorUnits, isLoadingUnits, toUnit, toUnitName, toUnitLongName }
  }
  

/**
 * 
 * @returns 
 */
export const usePests = () => {
  const  { 
      data: pests, 
      error: errorPests, 
      isLoading: isLoadingPests } = useQueryRetrievePests();
          
    const toPest = (pestID: string | undefined) => {
        return pests?.find(pest => pest.id === pestID)
    }

    const toPestName = (pestID: string | undefined) => {
        return toPest(pestID)?.name
    }

  return { pests, errorPests, isLoadingPests, toPest, toPestName };
}


/**
 * 
 * @returns 
 */
export const useCultureStages = () => {
  const  { 
      data: cultureStages, 
      error: errorCultureStages, 
      isLoading: isLoadingCultureStages } = useQueryRetrieveCultureStages();

  const toCultureStage = (cultureStageID: string | undefined) => {
      return cultureStages?.find(cultureStage => cultureStage.id === cultureStageID)
  }

  const toCultureStageName = (cultureStageID: string) => {
      return toCultureStage(cultureStageID)?.bbch
  }

  return { cultureStages, errorCultureStages, isLoadingCultureStages, toCultureStage, toCultureStageName };
}

/**
 * 
 */
export const useTractors = () => {
  const { 
    data: farmDetails, 
    error: errorTractors, 
    isLoading: isLoadingTractors 
  } = useQueryRetrieveFarmDetails();

  const tractors : Tractor[] = farmDetails?.tractors || [];

  const toTractor = useCallback((tractorID: string | undefined) => {
    return tractors?.find(tractor => tractor.tractorID === tractorID)
  }, [tractors]);

  const toTractorName = useCallback((tractorID: string | undefined) => {
    return toTractor(tractorID)?.tractorShortCode
  }, [toTractor]);

  return { tractors, errorTractors, isLoadingTractors, toTractor, toTractorName };
}

/**
 * 
 * @returns 
 */
export const useSprayers = () => {
  const { 
    data: farmDetails, 
    error: errorSprayers, 
    isLoading: isLoadingSprayers 
  } = useQueryRetrieveFarmDetails();
          
  const sprayers : Sprayer[] = farmDetails?.sprayers || [];

  const toSprayer = useCallback((sprayerID: string | undefined) => {
    return sprayers?.find(sprayer => sprayer.sprayerID === sprayerID)
  }, [sprayers]);

  const toSprayerName = useCallback((sprayerID: string | undefined) => {
    return toSprayer(sprayerID)?.sprayerName
  }, [toSprayer]);

  return { sprayers, errorSprayers, isLoadingSprayers, toSprayer, toSprayerName };
}
/**
 * 
 * @returns 
 */
export const useDrivers = () => {
  const { 
    data: farmDetails, 
    error: errorDrivers, 
    isLoading: isLoadingDrivers 
  } = useQueryRetrieveFarmDetails();
          
  const drivers : Driver[] = farmDetails?.drivers || [];

  const toDriver = useCallback((driverID: string | undefined) => {
    return drivers?.find(driver => driver.userID === driverID)
  }, [drivers]);

  const toDriverName = useCallback((driverID: string | undefined) => {
    return toDriver(driverID)?.fullName
  }, [toDriver]);

  return { drivers, errorDrivers, isLoadingDrivers, toDriver, toDriverName };
}
export const usePreparers = () => {
  const {
    data: farmDetails,
    isLoading: isLoadingPreparers,
    error: errorPreparers } = useQueryRetrieveFarmDetails();

  const preparers : Preparer[] = farmDetails?.preparers || [];

  const toPreparer = useCallback((preparerID: string | undefined) => {
    return preparers.find(preparer => preparer.userID === preparerID)
  }, [preparers]);

  const toPreparerName = useCallback((preparerID: string | undefined) => {
    return toPreparer(preparerID)?.fullName
  }, [toPreparer]);



  return { preparers, errorPreparers, isLoadingPreparers, toPreparer, toPreparerName };
}

 /**
   * 
   * @returns 
   */
 export const useFormulations = () => {
  const { data: formulations,
    error: errorFormulations,
    isLoading: isLoadingFormulations
   } = useQueryRetrieveFormulations();

  const toFormulation = (formulationID: string | undefined) => {
      if (formulations && formulationID)
          return formulations.filter(f=> f.id === formulationID)[0]
      else
          return undefined
  }

  const toFormulationName = (formulationID: string | undefined) => {
      return toFormulation(formulationID)?.shortName
  }
 
  const toFormulationLongName = (formulationID: string | undefined) => {
    return toFormulation(formulationID)?.name;
 }

  return { formulations, errorFormulations, isLoadingFormulations, toFormulation, toFormulationName, toFormulationLongName }
}

export const useIngredients = () => {
  const { data: ingredients,
    error: errorIngredients,
    isLoading: isLoadingIngredients } = useQueryRetrieveIngredients();

  const toIngredient = (ingredientID: string | undefined) => {
    return ingredients?.find(ingredient => ingredient.id === ingredientID)
  }

  const toIngredientName = (ingredientID: string | undefined) => {  
    return toIngredient(ingredientID)?.shortName
  }

  const toIngredientLongName = (ingredientID: string | undefined) => {
    return toIngredient(ingredientID)?.name
  }

  return { ingredients, errorIngredients, isLoadingIngredients, toIngredient, toIngredientName, toIngredientLongName } 
}

export const useCultures = () => {
  const { data: cultures,
    error: errorCultures,
    isLoading: isLoadingCultures } = useQueryRetrieveCultures();

  const toCulture = (cultureID: string | undefined) => {
    return cultures?.find(culture => culture.id === cultureID)
  }

  const toCultureName = (cultureID: string | undefined) => {  
    return toCulture(cultureID)?.name
  }

  return { cultures, errorCultures, isLoadingCultures, toCulture, toCultureName } 
}

export const useBlocks = () => {
  const { data: blocks,
    error: errorBlocks,
    isLoading: isLoadingBlocks } = useQueryRetrieveBlocks();

  const toBlock = (blockID: string | undefined) => {
    return blocks?.find(block => block.id === blockID)
  }

  const toBlockName = (blockID: string | undefined) => {  
    return toBlock(blockID)?.name
  }

  return { blocks, errorBlocks, isLoadingBlocks, toBlock, toBlockName } 
}

export const useVarieties = (cultureID: string | undefined) => {
  const { data: varieties,
    error: errorVarieties,
    isLoading: isLoadingVarieties } = useQueryRetrieveVarieties(cultureID || "");

  const toVariety = (varietyID: string | undefined) => {
    return varieties?.find(variety => variety.id === varietyID)
  }

  const toVarietyName = (varietyID: string | undefined) => {  
    return toVariety(varietyID)?.name
  }

  return { varieties, errorVarieties, isLoadingVarieties, toVariety, toVarietyName } 
}

export const useTrees = () => {
  const { data: trees,
    error: errorTrees,
    isLoading: isLoadingTrees } = useQueryRetrieveTrees();

  const toTree = (treeID: string | undefined) => {
    return trees?.find(tree => tree.id === treeID)
  }

  const toTreeName = (treeID: string | undefined) => {  
    return toTree(treeID)?.description
  }

  return { trees, errorTrees, isLoadingTrees, toTree, toTreeName } 
}